import { useEffect, useRef, useState } from 'react';
import type { ConversationAttachmentKind, ConversationAttachmentView } from '@scoutaround/script-breakdown-shared';

import { useModalFocus } from '../../lib/useModalFocus.js';
import { Icon, type IconName } from '../Icon.js';
import { formatBytes } from '../references/format.js';

/** What a chip shows for a file that has no picture of its own. */
export const ATTACHMENT_KIND_ICON: Readonly<Record<ConversationAttachmentKind, IconName>> = Object.freeze({
  IMAGE: 'image',
  PDF: 'pdf',
  DOCUMENT: 'note',
  OTHER: 'file',
});

/** Why a document is its name and size only (owner decision R1: originals are never served). */
export const PRIVATE_FILE_NOTE = 'Kept privately. Files cannot be opened or downloaded here yet.';

/** A signed preview URL lives an hour; take a newer one a little before that. */
const URL_REFRESH_MS = 45 * 60 * 1000;

/**
 * THE FIRST SIGNED URL, KEPT. The thread re-reads its newest page every few seconds
 * and every answer signs the previews again, so a new query string each time: an
 * `<img>` following the prop would download the same picture on every poll. This
 * keeps the URL it first showed and takes the newest one only when that is close to
 * expiring, or when loading it failed.
 */
function useStableUrl(latest: string | null): { src: string | null; onError: () => void; broken: boolean } {
  const [state, setState] = useState<{ src: string | null; at: number; broken: boolean }>(() => ({
    src: latest,
    at: Date.now(),
    broken: false,
  }));
  const latestRef = useRef(latest);
  latestRef.current = latest;

  useEffect(() => {
    if (latest === null || latest === state.src) return;
    if (state.src === null || Date.now() - state.at > URL_REFRESH_MS) {
      setState({ src: latest, at: Date.now(), broken: false });
    }
  }, [latest, state.src, state.at]);

  const onError = () => {
    const next = latestRef.current;
    setState((current) =>
      next !== null && next !== current.src
        ? { src: next, at: Date.now(), broken: false }
        : { ...current, broken: true },
    );
  };

  return { src: state.src, onError, broken: state.broken };
}

/** A file with no picture: its kind, its name and its size. Never a link. */
export function FileChip({
  kind,
  filename,
  byteSize,
  testId,
  children,
}: {
  kind: ConversationAttachmentKind;
  filename: string;
  byteSize: number;
  testId: string;
  children?: React.ReactNode;
}) {
  return (
    <span className="cv-file" data-testid={testId} title={PRIVATE_FILE_NOTE}>
      <Icon name={ATTACHMENT_KIND_ICON[kind]} size={16} />
      <span className="cv-file-name">{filename}</span>
      <span className="mm cv-file-size">{formatBytes(byteSize)}</span>
      {children}
    </span>
  );
}

function Thumbnail({
  attachment: a,
  testId,
  onOpen,
}: {
  attachment: ConversationAttachmentView;
  testId: string;
  onOpen: () => void;
}) {
  const { src, onError, broken } = useStableUrl(a.preview.thumbUrl);
  if (src === null || broken) {
    return <FileChip kind={a.kind} filename={a.filename} byteSize={a.byteSize} testId={`${testId}-file`} />;
  }
  return (
    <button
      type="button"
      className="cv-thumb"
      data-testid={testId}
      onClick={onOpen}
      aria-label={`View ${a.filename}`}
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={onError}
        width={a.preview.width ?? undefined}
        height={a.preview.height ?? undefined}
      />
    </button>
  );
}

function Viewer({ attachment: a, testId, onClose }: { attachment: ConversationAttachmentView; testId: string; onClose: () => void }) {
  const modal = useModalFocus<HTMLDivElement>(onClose);
  const { src, onError, broken } = useStableUrl(a.preview.largeUrl ?? a.preview.thumbUrl);
  const titleId = `${testId}-title`;
  return (
    <div
      ref={modal.ref}
      className="cv-viewer"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid={testId}
      onKeyDown={modal.onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="cv-viewer-frame">
        <div className="cv-viewer-head">
          <strong id={titleId} className="cv-file-name">{a.filename}</strong>
          <span className="mm cv-file-size">{formatBytes(a.byteSize)}</span>
          <button type="button" className="ctl" data-testid={`${testId}-close`} onClick={onClose}>
            Close
          </button>
        </div>
        {src !== null && !broken ? (
          <img src={src} alt={a.filename} onError={onError} className="cv-viewer-image" />
        ) : (
          <p className="mm" style={{ margin: 0 }}>This picture could not be loaded. Reload the page to try again.</p>
        )}
      </div>
    </div>
  );
}

/**
 * The files on a message, in the order they were attached: pictures as the server's
 * re-encoded previews (a click opens the large one), everything else as its name and
 * size. A concealed message never has any.
 */
export function MessageAttachments({
  attachments,
  testId,
}: {
  attachments: readonly ConversationAttachmentView[] | undefined;
  testId: string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const list = attachments ?? [];
  if (list.length === 0) return null;

  const pictures = list.filter((a) => a.preview.status === 'READY' && a.preview.thumbUrl !== null);
  const files = list.filter((a) => !pictures.includes(a));
  const viewing = pictures.find((a) => a.id === open) ?? null;

  return (
    <div className="cv-attachments" data-testid={testId}>
      {pictures.length > 0 ? (
        <div className="cv-thumbs">
          {pictures.map((a) => (
            <Thumbnail
              key={a.id}
              attachment={a}
              testId={`${testId}-image-${a.id}`}
              onOpen={() => {
                setOpen(a.id);
              }}
            />
          ))}
        </div>
      ) : null}
      {files.length > 0 ? (
        <div className="cv-files">
          {files.map((a) => (
            <FileChip key={a.id} kind={a.kind} filename={a.filename} byteSize={a.byteSize} testId={`${testId}-file-${a.id}`} />
          ))}
        </div>
      ) : null}
      {viewing ? (
        <Viewer
          attachment={viewing}
          testId={`${testId}-viewer`}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </div>
  );
}
